<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { get, debounce } from 'lodash-es';
import { BFormSelect } from 'bootstrap-vue-next';
import type { IColumnDefinition, RowData, ISelectColumn } from '../types';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { dataTableMessages, formMessages } from '../../../messages';
import { isEmptyValue } from '../../forms/useItpSelectProvider';
import { errorDetail } from '../../loading/errorDetail';

const props = defineProps<{
  modelValue: any;
  row: RowData;
  column: IColumnDefinition;
  autofocus?: boolean;
}>();
const emit = defineEmits(['update:modelValue', 'change']);

const col = computed(() => props.column as ISelectColumn);
const valField = col.value.valueField || 'id';
const txtField = col.value.textField || 'text';

const isSmartMode = computed(() => {
  return (
    col.value.type === 'select2' || typeof col.value.options === 'function'
  );
});

const asyncOptions = ref<any[]>([]);
const isLoading = ref(false);
const searchError = ref<unknown>(null);

const currentOptions = computed(() => {
  if (Array.isArray(col.value.options)) {
    return col.value.options;
  }
  return asyncOptions.value;
});

const runSearch = async (query: string, loading: (l: boolean) => void) => {
  if (typeof col.value.options === 'function') {
    loading(true);
    isLoading.value = true;
    try {
      const results = await col.value.options(query);
      searchError.value = null;
      asyncOptions.value = results || [];
    } catch (e) {
      console.error('Select2 search error', e);
      searchError.value = e;
      asyncOptions.value = [];
    } finally {
      loading(false);
      isLoading.value = false;
    }
  }
};

const SEARCH_DELAY_MS = 500;
const searchWhenTypingPauses = debounce(runSearch, SEARCH_DELAY_MS);
let dropdownOpen = false;

const onSearch = (query: string, loading: (l: boolean) => void) => {
  if (dropdownOpen) {
    searchWhenTypingPauses(query, loading);
  }
};

const onOpen = () => {
  dropdownOpen = true;
};

const onClose = () => {
  dropdownOpen = false;
  searchWhenTypingPauses.cancel();
};

onBeforeUnmount(() => searchWhenTypingPauses.cancel());

const normalizedOptions = computed(() => {
  const opts = currentOptions.value;

  return opts.map((opt: any) => {
    if (typeof opt === 'string') {
      return { value: opt, text: opt, raw: opt };
    }
    return {
      value: col.value.bindObject ? opt : get(opt, valField),
      text: get(opt, txtField),
      raw: opt,
    };
  });
});

const currentValue = computed({
  get: () => (isEmptyValue(props.modelValue) ? null : props.modelValue),
  set: (val) => emit('update:modelValue', val),
});

const nativeInput = ref<any>(null);
const smartInput = ref<any>(null);

onMounted(() => {
  if (props.autofocus) {
    if (nativeInput.value) {
      const el = nativeInput.value;
      if (el.element && el.element.focus) {
        el.element.focus();
      } else if (el.focus) {
        el.focus();
      }
    }
    if (smartInput.value?.searchEl) {
      smartInput.value.searchEl.focus();
    }
  }
});

const onChange = () => {
  emit('change', currentValue.value);
};

const reduceSmartValue = (option: any) => {
  if (col.value.bindObject) {
    return option;
  }
  return get(option, valField);
};
</script>

<template>
  <div v-if="isSmartMode" class="itp-smart-select-wrapper">
    <vSelect
      ref="smartInput"
      v-model="currentValue"
      :options="currentOptions"
      :label="txtField"
      :reduce="reduceSmartValue"
      :clearable="!column.required"
      :searchable="true"
      :placeholder="col.placeholder"
      :disabled="col.editorIsDisabled ? col.editorIsDisabled(row) : false"
      class="itp-smart-select size-sm"
      append-to-body
      @search="onSearch"
      @open="onOpen"
      @close="onClose"
      @option:selected="onChange"
    >
      <template #no-options="{ search, searching }">
        <span v-if="searchError" class="text-danger" role="alert"
          >{{ formMessages.searchFailed }} {{ errorDetail(searchError) }}</span
        >
        <template v-else-if="searching"
          >{{ dataTableMessages.noResultFor }} <em>{{ search }}</em></template
        >
        <template v-else>{{ dataTableMessages.typeToSearch }}</template>
      </template>
    </vSelect>
  </div>

  <BFormSelect
    v-else
    ref="nativeInput"
    v-model="currentValue"
    size="sm"
    :options="normalizedOptions"
    :disabled="col.editorIsDisabled ? col.editorIsDisabled(row) : false"
    @change="onChange"
    @click.stop
  />
</template>
